import React from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Package as packageIcon, Menu, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext'; // Adjust the path if needed

import DesktopIcon from '@/assets/desktopLogo.png';

import coverageIcon from '@/assets/icons/folder.svg?react';
// import packageIcon from '@/assets/icons/package.svg?react';
import claimsIcon from '@/assets/icons/paper.svg?react';
import analyticsIcon from '@/assets/icons/analytics.svg?react';
import settingsIcon from '@/assets/icons/settings.svg?react';
import supportIcon from '@/assets/icons/chat.svg?react';
import toast from 'react-hot-toast';

interface NavItem {
  to: string;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  text: string;
}

const mainNav: NavItem[] = [
  { to: '/customer/policies', icon: coverageIcon, text: 'My Policies' },
  { to: '/customer/orders', icon: packageIcon, text: 'Orders' },
  { to: '/customer/claims', icon: claimsIcon, text: 'My Claims' },
  // { to: '/customer/analytics', icon: analyticsIcon, text: 'Analytics' },
];

const secondaryNav: NavItem[] = [
  { to: '/customer/settings', icon: settingsIcon, text: 'Settings' },
  { to: '/customer/support', icon: supportIcon, text: 'Help & Support' },
];

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
  workspace?: {
    user: {name:string;email:string};
    mainNav: NavItem[];
    secondaryNav: NavItem[];
    logout: () => Promise<void>;
  };
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, toggleSidebar, workspace }) => {
  const auth = useAuth();
  const user = workspace?.user ?? auth.user;
  const logout = workspace?.logout ?? auth.logout;

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('You have been logged out. See you soon!');
    } catch (err: any) {
      console.error('Logout failed:', err);
      toast.error('Logout failed: ' + (err.message ?? 'Unknown error'));
    }
  };

  // Derive initials (e.g., "Khalid Shaikh" → "KS")
  const initials = user?.name
    ? user.name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()
    : '';

  return (
    <>
      {/* Mobile Hamburger */}
      <button
        onClick={toggleSidebar}
        className="tw-fixed tw-top-4 tw-left-4 tw-z-40 tw-p-2 tw-text-gray-400 hover:tw-bg-gray-800 tw-rounded-md lg:tw-hidden"
      >
        <Menu size={24} />
      </button>

      {/* Overlay */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="tw-fixed tw-inset-0 tw-z-30 tw-bg-black tw-bg-opacity-30 tw-backdrop-blur-sm lg:tw-hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <motion.div
        className={`
          tw-fixed tw-top-0 tw-left-0 tw-z-40 tw-h-screen tw-w-64
          tw-bg-[#121212] tw-flex tw-flex-col tw-transform tw-transition-transform
          tw-duration-300 tw-ease-in-out
          ${isOpen ? 'tw-translate-x-0' : '-tw-translate-x-full lg:tw-translate-x-0'}
        `}
      >
        {/* Close Button */}
        <button
          onClick={toggleSidebar}
          className="tw-absolute tw-top-4 tw-right-4 tw-p-1 tw-text-gray-400 tw-rounded-md hover:tw-bg-gray-800 lg:tw-hidden"
        >
          <X size={20} />
        </button>

        {/* Logo */}
        <div className="tw-flex tw-items-center tw-justify-center tw-py-7 tw-px-4">
          <img src={DesktopIcon} alt="Protega Logo" className="tw-h-auto" />
        </div>

        {/* Navigation */}
        <nav className="tw-flex-1 tw-flex tw-flex-col tw-py-4">
          {[...(workspace?.mainNav ?? mainNav), { to: '', icon: () => <></>, activeIcon: () => <></>, text: '' }].map(
            ({ to, icon: Icon, text }) =>
              text && (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `tw-relative tw-flex tw-items-center tw-mx-3 tw-mb-1 tw-px-4 tw-py-3 tw-rounded-md tw-transition-colors ${isActive
                      ? 'tw-bg-[#0abde3] tw-text-white'
                      : 'tw-text-gray-400 hover:tw-bg-gray-800 hover:tw-text-gray-200'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span className="tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-left-0 tw-h-5/6 tw-w-2 tw-bg-black tw-rounded-md" />
                      )}
                      <Icon
                        className={`tw-w-5 tw-h-5 tw-mr-3 ${isActive ? 'tw-text-[#202020]' : 'tw-text-[#88929F]'
                          }`}
                      />
                      <span
                        className={`tw-font-semibold ${isActive ? 'tw-text-[#202020]' : 'tw-text-gray-400'
                          }`}
                      >
                        {text}
                      </span>
                    </>
                  )}
                </NavLink>
              )
          )}

          <hr className="tw-border-t tw-border-gray-700 tw-my-4 tw-mx-3" />

          {(workspace?.secondaryNav ?? secondaryNav).map(({ to, icon: Icon, text }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `tw-relative tw-flex tw-items-center tw-mx-3 tw-mb-1 tw-px-4 tw-py-3 tw-rounded-md tw-transition-colors ${isActive
                  ? 'tw-bg-[#0abde3] tw-text-white'
                  : 'tw-text-gray-400 hover:tw-bg-gray-800 hover:tw-text-gray-200'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-left-0 tw-h-5/6 tw-w-2 tw-bg-black tw-rounded-md" />
                  )}
                  <Icon
                    className={`tw-w-5 tw-h-5 tw-mr-3 ${isActive ? 'tw-text-[#202020]' : 'tw-text-[#88929F]'
                      }`}
                  />
                  <span
                    className={`tw-font-semibold ${isActive ? 'tw-text-[#202020]' : 'tw-text-gray-400'
                      }`}
                  >
                    {text}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Avatar + Logout */}
        <div className="tw-px-4 tw-py-3 tw-mt-auto tw-flex tw-flex-col tw-gap-1">
          <div className="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-2 tw-rounded-md hover:tw-bg-gray-800 tw-cursor-pointer">
            <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-cyan-500/20 tw-flex tw-items-center tw-justify-center tw-text-[#00C5FB] tw-font-medium tw-text-sm">
              {initials}
            </div>
            <div className="tw-flex tw-flex-col">
              <span className="tw-text-sm tw-text-gray-200">{user?.name || 'Guest'}</span>
              <span className="tw-text-xs tw-text-gray-400">{user?.email}</span>
            </div>
          </div>

          {/* Logout styled like other nav items */}
          <button
            onClick={handleLogout}
            className="tw-relative tw-flex tw-items-center tw-mx-1 tw-px-4 tw-py-3 tw-rounded-md tw-transition-colors tw-text-red-400 hover:tw-bg-gray-800 hover:tw-text-red-300"
          >
            <LogOut className="tw-w-5 tw-h-5 tw-mr-3" />
            <span className="tw-font-semibold">Logout</span>
          </button>
        </div>
      </motion.div>
    </>
  );
};

export default Sidebar;
